import { useQuery } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Badge, Card, CardContent, CardHeader } from '@/components/ui';
import { skillsApi } from '@/lib/api';
import { useCurrentUser } from '@/lib/auth/AuthProvider';

/** Spec S09 §"Skill library" — PO-only view of every skill + version state. */
export function SkillLibrary() {
  const user = useCurrentUser();
  const { data, isLoading } = useQuery({
    queryKey: ['studio', 'skills'],
    queryFn: () => skillsApi.list(),
    enabled: Boolean(user?.productOwner),
  });

  if (!user?.productOwner) {
    return (
      <div className="p-6">
        <p className="text-body text-slate-600">
          Skill editing requires the Product Owner flag on your account.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <header>
        <h1 className="text-display text-brand-900">Agent skills</h1>
        <p className="mt-1 text-body text-slate-600">
          Each skill is an LLM prompt loaded at worker startup. Changes go
          through a sandbox before going live.
        </p>
      </header>

      <Card>
        <CardContent className="p-0">
          {isLoading && <p className="px-4 py-3 text-small text-slate-500">Loading…</p>}
          {data && (
            <table className="w-full text-body">
              <thead className="border-b border-slate-200 bg-slate-50 text-h4 text-slate-600">
                <tr>
                  <th className="px-4 py-2 text-left">Skill</th>
                  <th className="px-4 py-2 text-left">Active version</th>
                  <th className="px-4 py-2 text-left">Drafts</th>
                  <th className="px-4 py-2 text-left">Candidates</th>
                  <th className="w-8 px-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.map((skill) => (
                  <tr key={skill.skill_id} className="hover:bg-slate-50">
                    <td className="px-4 py-2 font-mono text-small text-slate-700">
                      {skill.skill_id}
                    </td>
                    <td className="px-4 py-2">
                      {skill.active_version ? (
                        <Badge variant="success">{skill.active_version}</Badge>
                      ) : (
                        <span className="text-small text-slate-400">none</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-small text-slate-700">{skill.drafts}</td>
                    <td className="px-4 py-2 text-small text-slate-700">
                      {skill.candidates > 0 ? (
                        <Badge variant="warning">{skill.candidates}</Badge>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-2 py-2">
                      <Link
                        to={`/studio/skills/${skill.skill_id}`}
                        className="flex items-center text-small text-accent-700 hover:text-accent"
                      >
                        Open
                        <ChevronRight className="h-3.5 w-3.5" aria-hidden />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
